import type { ReactElement } from "react";
import { Route } from "wouter";
import { RequireAuth } from "../../lib/auth/RequireAuth";
import { PageLoader } from "../../components/ui/Spinner";
import { SIM_ACTIVE } from "./flags";
import { useEnabledManifests, useSimReady } from "./registry";

/**
 * <Route> elements for every enabled feature, each behind RequireAuth. App
 * spreads them inside its <Switch>, after the existing routes and before
 * NotFound. While features are still loading, an unknown path shows a loader
 * instead of flashing NotFound.
 */
export function useSimRoutes(): ReactElement[] {
  const manifests = useEnabledManifests();
  const ready = useSimReady();
  if (!SIM_ACTIVE) return [];
  if (!ready) {
    return [
      <Route key="_sim:loading">
        <PageLoader />
      </Route>,
    ];
  }
  return manifests.flatMap((m) => [
    ...(m.routes ?? []).map((r) => (
      <Route key={`${m.id}:${r.path}`} path={r.path}>
        <RequireAuth>
          <r.component />
        </RequireAuth>
      </Route>
    )),
    // Signed-out pages (a manifest's publicRoutes): no RequireAuth.
    ...(m.publicRoutes ?? []).map((r) => (
      <Route key={`${m.id}:public:${r.path}`} path={r.path} component={r.component} />
    )),
  ]);
}
